import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Close, CreditCard, Payment } from '@mui/icons-material';
import { PaymentMethod } from '../lib/supabase';

interface PaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  paymentMethod?: PaymentMethod | null;
  onSave: (paymentMethod: Omit<PaymentMethod, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<void>;
  isEditing?: boolean;
}

const paymentTypes = [
  { value: 'credit_card', label: 'Credit Card', icon: <CreditCard /> },
  { value: 'debit_card', label: 'Debit Card', icon: <CreditCard /> },
  { value: 'paypal', label: 'PayPal', icon: <Payment /> },
  { value: 'e_wallet', label: 'E-Wallet', icon: <Payment /> },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: <Payment /> },
  { value: 'other', label: 'Other', icon: <Payment /> },
];

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  open,
  onClose,
  paymentMethod,
  onSave,
  isEditing = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'credit_card' as PaymentMethod['type'],
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load payment method data when editing
  useEffect(() => {
    if (paymentMethod && isEditing) {
      setFormData({
        name: paymentMethod.name,
        type: paymentMethod.type,
        is_active: paymentMethod.is_active,
      });
    } else {
      // Reset form for new payment method
      setFormData({
        name: '',
        type: 'credit_card',
        is_active: true,
      });
    }
    setErrors({});
  }, [paymentMethod, isEditing, open]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Payment method name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Failed to save payment method:', error);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Payment />
          {isEditing ? 'Edit Payment Method' : 'Add New Payment Method'}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Method Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., My MasterCard, Business PayPal"
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  label="Payment Type"
                >
                  {paymentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>


          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name.trim()}
        >
          {isEditing ? 'Update' : 'Add'} Payment Method
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentMethodModal;
